import { memo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HERO_IMAGE_SIZES, HERO_PUBLIC_PATH, HERO_WEBP_SRCSET } from '../../lib/heroImage';
import ConciergeLeadForm from './ConciergeLeadForm';

const HeroSection = memo(() => {
  const { t } = useTranslation();

  return (
    <section 
      className="relative flex h-[min(78vh,920px)] min-h-[520px] sm:min-h-[560px] items-start overflow-hidden bg-neutral-950 pt-16 sm:pt-16 md:pt-20 lg:pt-24 pb-12 sm:pb-14"
      aria-labelledby="hero-title"
    >
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <img
          src={HERO_PUBLIC_PATH}
          srcSet={HERO_WEBP_SRCSET}
          sizes={HERO_IMAGE_SIZES}
          width={1280}
          height={720}
          decoding="sync"
          fetchPriority="high"
          alt=""
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-950/90 via-neutral-950/70 to-primary-900/60" />
      </div>
      <div className="container relative z-10 mx-auto w-full max-w-screen-2xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-stretch gap-10 lg:flex-row lg:items-stretch lg:gap-16 xl:gap-20">
          <div className="max-w-xl text-white text-center lg:text-left lg:pt-1">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-200/95 mb-3">
              {t('site.name', 'RentaBahay')}
            </p>
            <h1 
              id="hero-title"
              className="text-balance text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold md:font-bold mb-4 sm:mb-5 lg:mb-6 leading-[1.12] tracking-tight"
            >
              Premium rental concierge for your next home in the Philippines
            </h1>
            <p className="mb-7 max-w-lg text-sm leading-relaxed text-white/90 sm:mb-8 sm:text-base md:mb-9 md:text-lg">
              Tell us what you need and we’ll hand-pick rental homes in Manila, Cebu, Davao and more—matched to your budget and lifestyle.
            </p>

            <dl className="grid grid-cols-1 xs:grid-cols-3 sm:grid-cols-3 gap-3 sm:gap-4 max-w-lg text-left">
              <div className="rounded-xl border border-white/20 bg-neutral-950/90 px-4 py-3.5 shadow-lg shadow-black/25 backdrop-blur-sm">
                <dt className="text-[11px] font-medium uppercase tracking-wide text-white">
                  Renters matched
                </dt>
                <dd className="mt-1 text-lg font-semibold text-white">
                  1,200+
                </dd>
              </div>
              <div className="rounded-xl border border-white/20 bg-neutral-950/90 px-4 py-3.5 shadow-lg shadow-black/25 backdrop-blur-sm">
                <dt className="text-[11px] font-medium uppercase tracking-wide text-white">
                  Verified listings
                </dt>
                <dd className="mt-1 text-lg font-semibold text-white">
                  Curated only
                </dd>
              </div>
              <div className="rounded-xl border border-white/20 bg-neutral-950/90 px-4 py-3.5 shadow-lg shadow-black/25 backdrop-blur-sm">
                <dt className="text-[11px] font-medium uppercase tracking-wide text-white">
                  Response time
                </dt>
                <dd className="mt-1 text-lg font-semibold text-white">
                  &lt; 24 hours
                </dd>
              </div>
            </dl>
          </div>

          <div className="w-full max-w-md lg:max-w-sm xl:max-w-md lg:ml-auto">
            <ConciergeLeadForm />
            <p className="mt-4 rounded-lg bg-neutral-950 px-3 py-2.5 text-center text-sm text-white shadow-sm ring-1 ring-white/15">
              <span className="text-neutral-100">Prefer to browse? </span>
              <Link
                to="/search"
                className="font-semibold text-sky-200 underline decoration-2 decoration-sky-200/90 underline-offset-4 hover:text-white hover:decoration-white"
              >
                View all listings
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
});

HeroSection.displayName = 'HeroSection';

export default HeroSection;